/**
 * Shape of a website submission as the CRM stores it.
 * Spec: docs/superpowers/specs/2026-09-01-crm-website-tab-design.md
 *
 * Pure by design: no database, no network, no token. The mapping from a
 * website row to the CRM's five promoted columns is the part most likely to
 * drift as the website adds fields, so it is the part that must be testable
 * on its own.
 */

export const SOURCE_TYPES = ["valuation", "subscriber", "buyer"] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

export const SUBMISSION_STATUSES = [
  "new",
  "reviewed",
  "contacted",
  "converted",
  "archived",
] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export const SOURCE_LABELS: Record<SourceType, string> = {
  valuation: "Valuation submissions",
  subscriber: "Newsletter subscribers",
  buyer: "Buyers list submissions",
};

/** Tab slugs, so the URL reads naturally and the type stays canonical. */
export const SOURCE_SLUGS: Record<SourceType, string> = {
  valuation: "valuations",
  subscriber: "subscribers",
  buyer: "buyers",
};

export interface DerivedFields {
  sourceId: string;
  submittedAt: Date;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  companyName: string | null;
  headline: string | null;
}

type Payload = Record<string, unknown>;

const str = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

/** Compact money for list rows: 4_200_000 -> "$4.2M". */
export function money(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n.toLocaleString()}`;
}

/** Host of a URL that may arrive bare ("acme.com") or full, minus any "www.". */
export function hostOf(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const u = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return raw;
  }
}

/** "independent_sponsor" -> "Independent Sponsor". */
export function humanise(raw: string | null): string | null {
  if (!raw) return null;
  return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function toDate(v: unknown): Date {
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) {
    throw new Error(`website submission has an unparseable timestamp: ${String(v)}`);
  }
  return d;
}

export function deriveSubmissionFields(sourceType: SourceType, payload: Payload): DerivedFields {
  const id = str(payload.id);
  if (!id) throw new Error("website submission payload has no id");

  switch (sourceType) {
    case "valuation": {
      const lo = money(payload.valuationLow);
      const hi = money(payload.valuationHigh);
      const range = lo && hi ? `${lo} – ${hi}` : null;
      return {
        sourceId: id,
        submittedAt: toDate(payload.submittedAt),
        contactName: str(payload.contactName),
        contactEmail: str(payload.contactEmail),
        contactPhone: str(payload.contactPhone),
        // companyName is nullable upstream while companyWebsite is NOT NULL,
        // so the host keeps an anonymous submission identifiable in a list.
        companyName: str(payload.companyName) ?? hostOf(str(payload.companyWebsite)),
        headline: range ?? "No estimate",
      };
    }

    case "subscriber":
      return {
        sourceId: id,
        submittedAt: toDate(payload.subscribedAt),
        contactName: str(payload.name),
        contactEmail: str(payload.email),
        contactPhone: null,
        companyName: null,
        headline: "Newsletter signup",
      };

    case "buyer":
      return {
        sourceId: id,
        // createdAt, not the updatedAt watermark: an edit long after
        // registration must not re-sort the row to the top of the list.
        submittedAt: toDate(payload.createdAt),
        contactName: str(payload.name),
        contactEmail: str(payload.email),
        contactPhone: str(payload.phone),
        companyName: str(payload.firmName),
        headline: humanise(str(payload.firmType)) ?? "Buyer registration",
      };

    default:
      throw new Error(`unknown source type: ${String(sourceType)}`);
  }
}
